// How the customer bundle morphs a page it renders.
//
// `entrypoints/users.js` replaces EVERY Turbo page render
// with a morphdom pass (`turbo:before-render` hands Turbo
// its own `render`), refreshes included. So on a customer
// page Turbo's Idiomorph never runs and its
// `turbo:before-morph-attribute` event never fires: a
// listener on it is a silent no-op. What a customer page
// keeps through a morph is decided HERE, and nowhere
// else. Extracted from the entrypoint so the rules can be
// exercised against a real morphdom without booting it.
//
// THE TYPED-STATE RULE (CAL-522). Applying a discount
// code posts a form that sits outside the checkout form
// and redirects back to the same URL, so Turbo renders
// the checkout again from HTML that never saw what the
// customer had typed or picked. morphdom already leaves
// alone a control whose markup came back identical, which
// is why a typed name used to survive; but any control
// whose markup changed had its value or `checked` synced
// to the server's, and applying a code changes exactly
// those: every tip tile carries its amount, so the tile
// the customer picked was cleared and their tip dropped
// out of the total they were about to pay.
//
// So inside a form that opts in with `KEEP_ATTRIBUTE`, a
// control the customer has changed since it was rendered
// keeps what they did, PROVIDED the server's own answer
// for it is the one it gave last time. The attributes are
// the server's word (`value`, `checked`), the properties
// are the customer's (`value`, `checked`), and a control
// whose attribute moves is one the server has something
// new to say about: the order form refused and echoed
// back, a saved address filled in by the address sheet's
// round trip. Those still apply. Everything else about
// the control (classes, `aria-*`, data attributes) is
// morphed as ever.
//
// Opt-in and per form, never page-wide: a form that posts
// to itself and expects an empty box back (a comment, a
// note) renders the same empty `value` it rendered
// before, and this rule would put the sent text back.
// The checkout form never submits to itself that way: it
// leaves for the order, or comes back refused with its
// values echoed. The discount code box claims a form of
// its own, so the server's answer always wins there.
//
// AN ECHO IS NOT A NEW ANSWER. An order refused with a
// 422 comes back with every field the customer posted
// written into its `value`, which is the server repeating
// what the box already holds. Taken as the server's new
// word, it would become the answer the NEXT render is
// compared with, and the fresh GET that follows a Remove
// (it knows nothing of the post) would read as the server
// changing its mind back to blank, wiping the name the
// refusal had just echoed. That is exactly the flow the
// refusal sentence sends the customer down ("Remove it to
// place your order"), measured on CAL-522. So where
// the server's new answer is what the control already
// holds, the control keeps the answer it had.
//
// That makes "what the control already holds" a contract
// with the server: a refused page echoes every box
// EXACTLY as it was typed. The phone used to come back in
// its canonical `+52` spelling, which this rule cannot
// recognise as an echo, so the Remove after it blanked
// the box; `BaseForm.phone_fields` keeps the typed value
// for the echo since. Never teach this file what a
// server normalizes: fix the echo instead.
//
// A RADIO GROUP IS SETTLED AS A GROUP, BEFORE THE MORPH
// TOUCHES ANYTHING. Its answer is spread over several
// nodes, and the browser keeps at most one of them
// checked: a radio that joins the group checked unchecks
// the rest, and a radio nobody has clicked follows its
// `checked` attribute whenever that attribute is written.
// morphdom does not always reach a group in place. When a
// section's position among its siblings moves (the refused
// order draws a notice above the payment section, the
// Remove takes it away) morphdom rebuilds the section as a
// NEW subtree: it attaches the server's radios while the
// customer's are still in the document, and only then
// swaps the keyed originals in. For that moment the
// server's checked radio is in the live group, and it
// unchecks the customer's pick before any per-control hook
// has read it. Measured on CAL-522: pick "Pay at pickup",
// place a refused order, press Remove, and both payment
// radios came back unchecked. A group the customer never
// touched could end empty the same way.
//
// So radios never go through the per-control rule. Before
// morphdom runs, `morphCustomerPage` reads each opted-in
// group whole (what the server rendered, what the customer
// picked, what the server answers now), applies the same
// rule to that one answer and absorbs an echo in the
// incoming tree. After the morph it writes every group
// back: the server's attributes, then the one answer by
// property, the server's included, since the same rebuild
// can leave the server's own radio unchecked too. Radios
// are paired across the two renders by value where every
// value is distinct (a payment rail, a reward, a table)
// and by position otherwise (the tip tiles, which all
// carry the default "on"), and a group whose size moved
// under positional pairing is the server's. A pick the
// server now renders disabled is the server's too: a
// disabled radio submits nothing.
//
// A SELECT'S ANSWER IS RECORDED, NEVER READ BACK. The
// rule needs what the server rendered last time, and a
// select cannot say: `state-selector` and
// `city-selector` rebuild their option lists with
// `innerHTML`, and the server's `selected` mark goes
// with them. So what the server answers for each select
// is written down when it is said, from the page as it
// was parsed (`rememberRenderedAnswers`, which
// `entrypoints/users.js` calls before any controller
// connects) and from every incoming page after that,
// and the rule compares with the record. A kept select
// keeps its OPTIONS too: the list the customer chose
// from is the one their answer belongs to (the cities of
// the state they picked), where the server's is the
// prompt alone for a guest, or the cities of the state
// it last knew. Measured on CAL-522: pick an address on
// the delivery checkout, apply a code, and the state and
// city came back blank, taking the address row, the
// quote and the Place order button with them.
//
// Hidden inputs are left to the server and to the
// controllers that own them (`whatsapp-mirror`,
// `tip-selector`, `courier-quote`): each owner rebuilds
// its field from the visible controls when the morph
// reconnects it, and a courier quote is priced on the
// basket a code has just changed, so it has to be asked
// for again. The exception is a hidden field holding an
// answer only the customer gave and nothing on the page
// can rebuild, the drop-off pin a pick or a drag placed:
// it carries `KEEP_ATTRIBUTE` itself, and is recorded
// the way a select is, since its value IS its attribute.
//
// A TOAST IS NOT PART OF THE PAGE. Toastify draws it
// straight into the body, ahead of the first child, and
// no server page carries one, so nothing incoming pairs
// with it: it was discarded by every render (a toast
// showing when the checkout loaded vanished as a code
// was applied), and where the body's first child is an
// unkeyed element of the same tag it was worse, since
// morphdom paired the toast WITH that element and
// Toastify's timer then removed the page. So a toast
// gets a key of its own (nothing on the server side can
// match it) and is never discarded. Only a node Toastify
// drew qualifies: a permanent node the server drew and
// has stopped drawing is gone as ever.

import morphdom from "morphdom";

export const KEEP_ATTRIBUTE = "data-keep-typed-state";

const PERMANENT_ATTRIBUTE = "data-morph-permanent";

// Controls whose live state this morph has decided to
// keep, so the children hook below can skip the value
// and `checked` sync morphdom performs there.
const kept = new WeakSet();

// What the server last rendered for each recorded
// control (a select, a hidden field that opts in),
// keyed by form id and name. See "A select's answer is
// recorded" above.
let recordedRenders = new Map();

// The recorded controls this morph kept, so the record
// can go on holding the answer they were compared with.
const keptRecords = new Set();

const UNKEPT_INPUT_TYPES = new Set([
  "file",
  "submit",
  "button",
  "image",
  "reset",
]);

function optedIn(element) {
  const form = element.form;
  return Boolean(form && form.hasAttribute(KEEP_ATTRIBUTE));
}

// A control's form, read the same way in a live tree and
// a detached one: by its `form` attribute, else its
// enclosing form. `element.form` does not resolve a
// claimed form in a detached tree.
function formIdOf(element) {
  return element.getAttribute("form") ?? element.closest("form")?.id;
}

function recordKey(element) {
  const formId = formIdOf(element);
  if (!formId || !element.name) return null;

  return `${formId}\u0000${element.name}`;
}

// A control whose rendered answer has to be recorded
// because its markup cannot be trusted to keep it.
function recorded(element) {
  if (element instanceof HTMLSelectElement) {
    return !element.multiple;
  }

  return (
    element instanceof HTMLInputElement &&
    element.type === "hidden" &&
    element.hasAttribute(KEEP_ATTRIBUTE)
  );
}

// The server's answer, from markup nothing has touched:
// the option it marked, else the one a browser falls back
// to, the first.
function renderedAnswer(element) {
  if (element instanceof HTMLSelectElement) {
    const options = [...element.options];
    const marked = options.filter((option) =>
      option.hasAttribute("selected")
    );
    const answer = marked.pop() ?? options[0];
    return answer ? answer.value : "";
  }

  return element.getAttribute("value") ?? "";
}

// Every recorded control of an opted-in form under
// `root`, with the answer its markup gives.
function recordedAnswers(root) {
  const answers = new Map();
  const formIds = new Set(
    [...root.querySelectorAll(`form[${KEEP_ATTRIBUTE}]`)]
      .map((form) => form.id)
      .filter(Boolean)
  );
  if (formIds.size === 0) return answers;

  root.querySelectorAll("select, input[type=hidden]").forEach(
    (element) => {
      if (!recorded(element)) return;
      if (!formIds.has(formIdOf(element))) return;

      const key = recordKey(element);
      if (key) answers.set(key, renderedAnswer(element));
    }
  );

  return answers;
}

// Writes down what the page as parsed answers for every
// recorded control. `entrypoints/users.js` calls it
// before Stimulus connects anything, which is the last
// moment the markup is still the server's.
export function rememberRenderedAnswers(root) {
  recordedRenders = recordedAnswers(root);
}

// Which of the four shapes of kept control this is, or
// null when the rule does not reach it at all.
//
//   text      a textarea: its answer is its text
//   choice    a checkbox: its answer is `checked`
//   value     any other typed input: its answer is
//             `value`
//   recorded  a select, or a hidden field that opts in:
//             its answer is `value`, compared with the
//             record rather than the markup
//
// A radio is none of these: its group is settled whole
// by `morphCustomerPage` (see "A radio group is settled
// as a group" above).
function keptKind(fromEl, toEl) {
  if (!toEl || fromEl.nodeName !== toEl.nodeName) return null;

  if (fromEl instanceof HTMLTextAreaElement) {
    return optedIn(fromEl) ? "text" : null;
  }

  if (fromEl instanceof HTMLSelectElement) {
    return recorded(fromEl) && optedIn(fromEl)
      ? "recorded"
      : null;
  }

  if (!(fromEl instanceof HTMLInputElement)) return null;
  if (UNKEPT_INPUT_TYPES.has(fromEl.type)) return null;
  if (fromEl.type === "radio") return null;
  if (fromEl.type !== toEl.type || !optedIn(fromEl)) return null;

  if (fromEl.type === "hidden") {
    return recorded(fromEl) ? "recorded" : null;
  }

  if (fromEl.type === "checkbox") return "choice";

  return "value";
}

// Where the server's new answer is what the control
// already holds, rewrites the INCOMING control's answer
// back to the one it replaces (see "An echo is not a new
// answer" above). The detached new tree is Turbo's to
// throw away, so writing to it costs nothing.
export function absorbEcho(fromEl, toEl) {
  const kind = keptKind(fromEl, toEl);

  if (kind === "text") {
    const echoed = toEl.defaultValue === fromEl.value;
    if (echoed && toEl.defaultValue !== fromEl.defaultValue) {
      toEl.defaultValue = fromEl.defaultValue;
    }
  } else if (kind === "choice") {
    const answer = toEl.hasAttribute("checked");
    if (answer === fromEl.checked && answer !== fromEl.defaultChecked) {
      toEl.toggleAttribute("checked", fromEl.defaultChecked);
    }
  } else if (kind === "value") {
    const answer = toEl.getAttribute("value");
    const base = fromEl.getAttribute("value");
    if ((answer ?? "") === fromEl.value && answer !== base) {
      if (base === null) {
        toEl.removeAttribute("value");
      } else {
        toEl.setAttribute("value", base);
      }
    }
  }
}

// Did the customer change this control since the server
// rendered it, and did the server just render the same
// answer for it again?
export function keepsTypedState(fromEl, toEl) {
  switch (keptKind(fromEl, toEl)) {
    case "text":
      return (
        toEl.defaultValue === fromEl.defaultValue &&
        fromEl.value !== fromEl.defaultValue
      );
    case "choice":
      return (
        toEl.hasAttribute("checked") === fromEl.defaultChecked &&
        fromEl.checked !== fromEl.defaultChecked
      );
    case "value":
      return (
        toEl.getAttribute("value") === fromEl.getAttribute("value") &&
        fromEl.value !== fromEl.defaultValue
      );
    case "recorded":
      return keepsRecordedAnswer(fromEl, toEl);
    default:
      return false;
  }
}

// The same rule against the record. The echo is part of
// it rather than rewritten into the incoming node: the
// record simply goes on holding what the server said
// before (see `morphCustomerPage`).
function keepsRecordedAnswer(fromEl, toEl) {
  const key = recordKey(fromEl);
  if (!key || !recordedRenders.has(key)) return false;

  const before = recordedRenders.get(key);
  const answer = renderedAnswer(toEl);
  const current = fromEl.value;

  if (current === before) return false;

  return answer === before || answer === current;
}

// A kept hidden field's value IS its `value` attribute,
// which morphdom copies from the server's before any
// hook can stop it, so the incoming node takes the
// customer's answer first. A select needs nothing here:
// its answer lives in the options, which a kept control
// does not have morphed.
function holdRecordedAnswer(fromEl, toEl) {
  keptRecords.add(recordKey(fromEl));

  if (fromEl instanceof HTMLInputElement) {
    toEl.setAttribute("value", fromEl.value);
  }
}

export function onBeforeElUpdated(fromEl, toEl) {
  if (typeof fromEl !== "object" || !fromEl.hasAttribute) return true;

  absorbEcho(fromEl, toEl);

  const keeps = keepsTypedState(fromEl, toEl);
  if (keeps && keptKind(fromEl, toEl) === "recorded") {
    holdRecordedAnswer(fromEl, toEl);
  }

  // Identical markup is left alone entirely, live state
  // and all, as it always was.
  if (fromEl.isEqualNode(toEl)) return false;

  if (
    fromEl.hasAttribute(PERMANENT_ATTRIBUTE) &&
    toEl.hasAttribute(PERMANENT_ATTRIBUTE)
  ) {
    return false;
  }

  if (keeps) kept.add(fromEl);

  return true;
}

// A node Toastify drew: permanent, and never in a
// server page. See "A toast is not part of the page".
function isToast(node) {
  return (
    node.nodeType === Node.ELEMENT_NODE &&
    node.classList.contains("toastify") &&
    node.hasAttribute(PERMANENT_ATTRIBUTE)
  );
}

const toastKeys = new WeakMap();
let toastCount = 0;

// morphdom's own key (the id), and for a toast one no
// incoming node can carry, so it is never paired with a
// page element by tag name.
export function getNodeKey(node) {
  if (!node) return undefined;

  const id = (node.getAttribute && node.getAttribute("id")) || node.id;
  if (id || !isToast(node)) return id;

  if (!toastKeys.has(node)) {
    toastCount += 1;
    toastKeys.set(node, `customer-morph-toast-${toastCount}`);
  }
  return toastKeys.get(node);
}

export function onBeforeNodeDiscarded(node) {
  return !isToast(node);
}

// morphdom syncs an input's value and `checked`, and a
// textarea's text, AFTER this hook and only if it does
// not answer false. The attributes have already been
// morphed by then, so a kept control still takes every
// change the server made to it except its value.
export function onBeforeElChildrenUpdated(fromEl) {
  if (!kept.has(fromEl)) return true;

  kept.delete(fromEl);
  return false;
}

export const customerMorphOptions = {
  getNodeKey,
  onBeforeElUpdated,
  onBeforeElChildrenUpdated,
  onBeforeNodeDiscarded,
};

// The radios of every opted-in form under `root`, grouped
// the way the browser groups them: one form, one name.
// The form is found by id in BOTH trees, and a radio may
// claim it by its `form` attribute from outside (the table
// sheet does). `radio.form` is not used: a detached tree
// does not resolve a claimed form, and the two trees have
// to be read the same way.
function radioGroups(root) {
  const groups = new Map();
  const formIds = new Set(
    [...root.querySelectorAll(`form[${KEEP_ATTRIBUTE}]`)]
      .map((form) => form.id)
      .filter(Boolean)
  );
  if (formIds.size === 0) return groups;

  root.querySelectorAll('input[type="radio"]').forEach((radio) => {
    const formId =
      radio.getAttribute("form") ?? radio.closest("form")?.id;
    if (!radio.name || !formIds.has(formId)) return;

    const key = `${formId}\u0000${radio.name}`;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(radio);
  });

  return groups;
}

const distinctValues = (radios) =>
  new Set(radios.map((radio) => radio.value)).size === radios.length;

const byValue = (radios, radio) => radio.value;
const byPosition = (radios, radio) => String(radios.indexOf(radio));

// How one group's radios are told apart across two
// renders, or null when they cannot be: by value where
// every value is distinct in both, else by position,
// which only holds while the group keeps its size.
function pairing(from, to) {
  if (distinctValues(from) && distinctValues(to)) return byValue;
  if (from.length !== to.length) return null;

  return byPosition;
}

function answerOf(radios, keyOf, isAnswer) {
  const radio = radios.find(isAnswer);
  return radio ? keyOf(radios, radio) : null;
}

const rendered = (radio) => radio.hasAttribute("checked");

// The echo rule for a group: the incoming radios take
// back, attribute for attribute, the answer the server
// gave last time.
function absorbGroupEcho(from, to, keyOf) {
  to.forEach((radio) => {
    const key = keyOf(to, radio);
    const was = from.find((old) => keyOf(from, old) === key);
    const answer = was ? was.getAttribute("checked") : null;

    if (answer === null) {
      radio.removeAttribute("checked");
    } else {
      radio.setAttribute("checked", answer);
    }
  });
}

// The customer's pick in this group, as the incoming
// radio that carries it, when the typed-state rule keeps
// it (absorbing an echo on the way); null when the
// server's answer stands.
function keptPick(from, to) {
  const keyOf = from && pairing(from, to);
  if (!keyOf) return null;

  const before = answerOf(from, keyOf, rendered);
  const picked = answerOf(from, keyOf, (radio) => radio.checked);
  const answer = answerOf(to, keyOf, rendered);

  // Untouched, or the server has something new to say.
  if (picked === null || picked === before) return null;
  if (answer !== picked && answer !== before) return null;

  const target = to.find((radio) => keyOf(to, radio) === picked);
  if (!target || target.disabled) return null;

  if (answer === picked) absorbGroupEcho(from, to, keyOf);
  return target;
}

// Decides every opted-in group of the INCOMING page before
// morphdom moves anything: which radio ends checked, and
// the server's attributes, both read while nothing has yet
// been disturbed. morphdom writes each radio's `checked`
// attribute back from the incoming radio's PROPERTY, and
// that property is exactly what the group exclusion
// corrupts while the incoming radios sit in the live
// document, so neither half can be read after the morph.
function settleRadioGroups(fromEl, toEl) {
  const current = radioGroups(fromEl);
  const settled = [];

  radioGroups(toEl).forEach((to, group) => {
    const keyOf = distinctValues(to) ? byValue : byPosition;
    const pick = keptPick(current.get(group), to);
    const final = pick
      ? keyOf(to, pick)
      : answerOf(to, keyOf, rendered);
    const attributes = new Map(
      to.map((radio) => [
        keyOf(to, radio),
        radio.getAttribute("checked"),
      ])
    );

    settled.push({ group, keyOf, final, attributes });
  });

  return settled;
}

// Puts every settled group back: the server's attributes
// first (a radio nobody clicked follows them), then the
// one answer by property. The server's answer needs this
// as much as a kept pick does: measured in the same
// rebuild, a group nobody touched came back empty.
function restoreRadioGroups(root, settled) {
  const groups = radioGroups(root);

  settled.forEach(({ group, keyOf, final, attributes }) => {
    const radios = groups.get(group);
    if (!radios || radios.length !== attributes.size) return;

    radios.forEach((radio) => {
      const answer = attributes.get(keyOf(radios, radio));
      if (answer === undefined) return;
      if (radio.getAttribute("checked") === answer) return;

      if (answer === null) {
        radio.removeAttribute("checked");
      } else {
        radio.setAttribute("checked", answer);
      }
    });

    const radio = radios.find((r) => keyOf(radios, r) === final);
    if (radio) {
      radio.checked = true;
    } else {
      radios.forEach((r) => (r.checked = false));
    }
  });
}

// The record the NEXT render is compared with: what the
// server answered this time, except for a control whose
// customer answer was kept, where it goes on holding
// the answer that was compared with (an echo absorbed,
// or the same answer again).
function recordRenderedAnswers(incoming) {
  const next = new Map();

  incoming.forEach((answer, key) => {
    const keptBefore = keptRecords.has(key) && recordedRenders.has(key);
    next.set(key, keptBefore ? recordedRenders.get(key) : answer);
  });

  recordedRenders = next;
  keptRecords.clear();
}

// The customer bundle's page render: `entrypoints/users.js`
// hands Turbo this for every visit. Radio groups are
// settled before morphdom moves anything and put back
// after it, so it runs before the controllers reconnect.
// The incoming page's recorded answers are read before
// morphdom moves any of its nodes into the document.
export function morphCustomerPage(fromEl, toEl) {
  const settled = settleRadioGroups(fromEl, toEl);
  const incoming = recordedAnswers(toEl);

  keptRecords.clear();
  morphdom(fromEl, toEl, customerMorphOptions);

  restoreRadioGroups(fromEl, settled);
  recordRenderedAnswers(incoming);
}
